import React, { useState, useEffect } from 'react';
import { createReviewRating, checkReviewEligibility } from '../../../../services/reviewRating';
import ThankYouPage from './ThankYouPage';

const getRatingLabel = (rating) => {
  if (rating <= 1) return { label: "Poor", icon: <i className="bi bi-emoji-frown fs-4 text-danger me-2"></i> };
  if (rating <= 2) return { label: "Fair", icon: <i className="bi bi-emoji-neutral fs-4 text-warning me-2"></i> };
  if (rating <= 3) return { label: "Good", icon: <i className="bi bi-emoji-smile fs-4 text-info me-2"></i> };
  if (rating <= 4) return { label: "Very Good", icon: <i className="bi bi-emoji-laughing fs-4 text-primary me-2"></i> };
  return { label: "Excellent", icon: <i className="bi bi-emoji-heart-eyes fs-4 text-success me-2"></i> };
};

const ReviewRatingModal = ({ onClose, dashboardReady = {} }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isEligibleMessage, setIsEligibleMessage] = useState('');

  // Error states
  const [ratingError, setRatingError] = useState('');
  const [feedbackError, setFeedbackError] = useState('');

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const response = await checkReviewEligibility();
        if (response) {
          setIsEligible(response?.eligible)
          setIsEligibleMessage(response?.message);
        }
      } catch (e) {
        console.error("Error checking eligibility:", e);
      }
    };
    checkEligibility();
  }, []);

  const handleStarClick = (e, index) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - left;
    const clickedValue = x < width / 2 ? index + 0.5 : index + 1;
    setRating(clickedValue);
    setRatingError('');
  };

  const handleStarHover = (e, index) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - left;
    const hoveredValue = x < width / 2 ? index + 0.5 : index + 1;
    setHoverRating(hoveredValue);
  };

  const renderStars = () => {
    const value = hoverRating || rating;

    return [0, 1, 2, 3, 4].map((index) => {
      let starClass = "star";
      if (value >= index + 1) {
        starClass += " full";
      } else if (value >= index + 0.5) {
        starClass += " half";
      }

      return (
        <span
          key={index}
          className={starClass}
          onMouseMove={(e) => handleStarHover(e, index)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={(e) => handleStarClick(e, index)}
          role="button"
          aria-label={`Rate ${index + 1} star`}
        >
          <i className={`bi ${value >= index + 0.5 ? "bi-star-fill" : "bi-star"}`}></i>
        </span>
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;
    setRatingError('');
    setFeedbackError('');

    if (rating === 0) {
      setRatingError('Rating is required');
      hasError = true;
    }

    if (!feedback.trim()) {
      setFeedbackError('Review is required');
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      const payload = {
        review: {
          rating,
          comment: feedback,
        },
      };

      const response = await createReviewRating(payload);
      if (response) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        console.error('Failed to submit review.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isEligible) {
    return (
      <div className="review-modal-overlay">
        <div className="review-modal">
          <button className="close-button-icon" onClick={onClose} disabled={loading}><i className="bi bi-x-lg"></i></button>
          <ThankYouPage title={"Already Give Review and Rating"} text={isEligibleMessage} />
        </div>
      </div>
    );
  }

  return (
    <div className="review-modal-overlay">
      <div className="review-modal">
        <button className="close-button-icon" onClick={onClose} disabled={loading}><i className="bi bi-x-lg"></i></button>
        {!submitted ? (
          <>
            <h2 >Customer Reviews</h2>
            <p>We'd love your feedback to improve your experience!</p>

            <form onSubmit={handleSubmit}>
              <div className="stars">{renderStars()}</div>
              {rating > 0 && (
                <div className="rating-message">
                  <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }} className="d-flex align-items-center justify-content-center">
                    {getRatingLabel(rating).icon} {getRatingLabel(rating).label} ({rating} / 5)
                  </span>
                </div>
              )}
              {ratingError && <div className="text-danger mb-1">{ratingError}</div>}

              <textarea
                placeholder="Write your feedback..."
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  setFeedbackError('');
                }}
                className="feedback-box"
                disabled={loading}
              />
              {feedbackError && <div className="text-danger">{feedbackError}</div>}

              <div className="d-flex gap-3">
                <button type="button" className="btn review-button" onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn review-button-s" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <ThankYouPage />
        )}
      </div>
    </div>
  );
};

export default ReviewRatingModal;
